/* eslint-disable */
import Vue from 'vue';
import { ObserveVisibility } from 'vue-observe-visibility';
import axios from 'axios';
// import * as VueGoogleMaps from 'vue2-google-maps';
import GmapVue from 'gmap-vue';
import VCalendar from 'v-calendar';
import CountryFlag from 'vue-country-flag';
import VueTelInput from 'vue-tel-input';
import Tooltip from 'vue-directive-tooltip';
import 'vue-directive-tooltip/dist/vueDirectiveTooltip.css';
import 'vue-tel-input/dist/vue-tel-input.css';
import VueApexCharts from 'vue-apexcharts'
import VueDatamaps from 'vue-datamaps'
import App from './App.vue';
import router from './router';
import store from './store';
import Icon from './components/elements/icons/index.vue';
import message from './components/global/message/index.vue';
import i18n from './i18n';
import { DateTime, Settings } from 'luxon';
import localesByCurrency from '@/data/localesByCurrency';

import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";

Vue.directive('observe-visibility', ObserveVisibility);
Vue.use(Tooltip);

Vue.config.productionTip = false;

// Send cookies (refresh_token httpOnly cookie) with every request
axios.defaults.withCredentials = true;

let axiosDynamicBaseUrl = `${window.location.protocol}//${window.location.hostname}/admin/api`;

axios.defaults.baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? process.env.VUE_APP_API_URL : axiosDynamicBaseUrl;

// before a request is made start the nprogress
axios.interceptors.request.use(config => {
  const skipUrls = [
    'progress-track',
    'get-statistic',
    'get-user-news',
    'mark-seen',
    'get-user-notifications',
    'webhook-actions/deliveries' // polled every 5s on the webhook detail page
  ]

  if (!skipUrls.some(skipUrl => config.url.includes(skipUrl))) {
    NProgress.start()
  }

  return config
})

// 401 refresh + retry logic
let isRefreshing = false
let failedQueue  = []

function processQueue (error, token = null) {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

axios.interceptors.response.use(function (response) {
  if (response.config.method === 'delete') {
    if (location.pathname.substring(1) === 'admin/shipments/book-shipment') {
      //Do not show delete message on draft delete from booking page
      return;
    }
    store.commit('SHOW_NOTIFICATION_BAR', {
      message: i18n.t('deleted_successfully'),
      type: 'success-window',
      iconName: 'check',
    });
  }
  NProgress.done()
  return response;
}, function (error) {

  const originalRequest = error.config

  if (error?.response?.status === 429) {
    store.commit('SHOW_NOTIFICATION_BAR', {
      message: 'Too many login attempts. Please wait a moment and try again.',
      type: 'alert-window',
    })
    NProgress.done()
    return Promise.reject(error)
  }

  if (
    error?.response?.status === 401 &&
    !originalRequest?._retry &&
    !originalRequest?.url?.includes('refresh-token') &&
    !originalRequest?.skipAuthRedirect &&
    router.currentRoute?.path !== '/login'
  ) {
    if (isRefreshing) {
      // Queue this request until the in-flight refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then(token => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`
        return axios(originalRequest)
      }).catch(err => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    return new Promise((resolve, reject) => {
      axios.post('refresh-token', {}, { withCredentials: true })
        .then(res => {
          const token = res?.data?.data?.token
          if (token) {
            store.dispatch('STORE_TOKEN', token)
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            processQueue(null, token)
            resolve(axios(originalRequest))
          } else {
            processQueue(new Error('No token'), null)
            store.commit('LOGOUT')
            router.push('/login')
            reject(error)
          }
        })
        .catch(refreshError => {
          processQueue(refreshError, null)
          store.commit('LOGOUT')
          router.push('/login')
          reject(refreshError)
        })
        .finally(() => {
          isRefreshing = false
        })
    })
  }

  if (error?.response && !error?.config?.skipAuthRedirect && error?.response?.status !== 401) {
    store.commit('SHOW_NOTIFICATION_BAR', {
      message: error.response.data.message,
      type: 'alert-window',
    });
  }

  NProgress.done()
  return Promise.reject(error);
});

// subscribe to mutation
const unsubscribe = store.subscribe((mutation) => {
  if(mutation.type === 'STORE_USER') {
    const lang = mutation.payload.language === 'dk' ? 'da' : 'en';
    i18n.locale = mutation.payload.language;
    Settings.defaultLocale = lang
  }
});

// GLOBAL FILTERS
Vue.filter('forNum', (value, locale) => {
  if (typeof value !== "number") {
    return value;
  }
  if (!locale) {
    locale = 'da-DK';
  }
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2
  });
  return formatter.format(value);
});

// Used to format currency and covert value all over the app
// If currency is present, conversion is not available, raw value will be formatted
// If currency param is not present and conversion data are present, value will be converted if is set so
Vue.filter('currency', (value, data, currency) => {
  if (typeof value !== "number") {
    return value;
  }

  //Logged user data
  let user = store?.state?.user?.info;

  if (!data) {
    //Use default user currency conversion data with current date
    data = user.currency_conversion
  }

  if (!currency && data) {
    currency = data.to_currency;
    //Convert value if backoffice master currency is not equal user currency
    if (data.need_conversion && data.from_to_currency_rate) {
      value = value * data.from_to_currency_rate;
      //later we will need usd to master/user
    }
  }

  //Use default value
  if (!currency) {
    currency = 'DKK';
  }

  let formatterOptions = {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }

  if (['DKK', 'NOK'].includes(currency)) {
    formatterOptions.currencyDisplay = 'code';
  }

  const locale = localesByCurrency[currency] ? localesByCurrency[currency] : 'dk-DK';

  try {
    const formatter = new Intl.NumberFormat(locale, formatterOptions);
    return formatter.format(parseFloat(value));
  } catch (e) {
    console.error('Currency filter error:', e, 'currency:', currency, 'value:', value);
    return value; // fallback
  }
});
Vue.filter('weight', (value, locale) => {
  if (typeof value !== "number") {
    return value;
  }
  if (!locale) {
    locale = 'da-DK';
  }
  const formatter = new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'kilogram',
    minimumFractionDigits: 2
  });
  return formatter.format(value);
});

Vue.filter('dateTime', (value, toFormat) => {
  if (!toFormat) {
    toFormat = localStorage.getItem('date_format') ?? 'dd.MM.yyyy';
  }
  if (value) {
    return DateTime.fromSQL(value).toFormat(toFormat)
  }
  return '-';
});



// GLOBAL components
Vue.component('Icon', Icon);
Vue.component('message', message);
Vue.component('apexchart', VueApexCharts)
Vue.component('vue-country-flag', CountryFlag);
Vue.use(VueTelInput);
Vue.use(VueDatamaps)
Vue.use(VCalendar);
Vue.use(VueApexCharts);
Vue.use(GmapVue, {
  load: {
    key: process.env.VUE_APP_GOOGLE_KEY,
    libraries: 'places',
    v: '3.26',
    dynamicLoad: false,
  },
})

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App)
}).$mount('#app');
